import { CobaltButton } from '@cae/cobalt-react/components/button';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconFilterOutline } from '@cae/cobalt-react/icons/outline/CobaltIconFilterOutline';
import { CobaltIconFilterSolid } from '@cae/cobalt-react/icons/solid/CobaltIconFilterSolid';
import { useInvoicesFilter } from '@features/invoices/contexts/filter/useInvoicesFilter';
import * as React from 'react';

import type { InvoicesFilterButtonProps } from './InvoicesFilterButton.types';

const InvoicesFilterButton = React.memo(
  ({
    onFiltersOpen,
    disabled = false,
  }: Readonly<InvoicesFilterButtonProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);
    const { filters } = useInvoicesFilter();

    const totalAppliedFilters = React.useMemo(
      () =>
        filters.balanceStatuses.length +
        filters.currencies.length +
        filters.platforms.length +
        filters.locations.length +
        (filters.dueDateStart !== null || filters.dueDateEnd !== null ? 1 : 0) +
        (filters.issuedDateStart !== null || filters.issuedDateEnd !== null ? 1 : 0),
      [filters]
    );

    const ariaLabel = React.useMemo(
      (): string =>
        totalAppliedFilters > 0
          ? `Filters (${totalAppliedFilters} applied)`
          : 'Filters',
      [totalAppliedFilters]
    );

    return (
      <CobaltButton
        variant="ghost"
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid="invoices-filter-button"
        onClick={onFiltersOpen}
      >
        {totalAppliedFilters === 0 ? (
          <CobaltIconFilterOutline />
        ) : (
          <CobaltIconFilterSolid />
        )}
        {!isMobileOrTablet
          ? `Filters${totalAppliedFilters > 0 ? ` (${totalAppliedFilters})` : ''}`
          : null}
      </CobaltButton>
    );
  }
);

InvoicesFilterButton.displayName = 'InvoicesFilterButton';

export { InvoicesFilterButton };
